import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp, orderBy, query } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes } from 'react-icons/fa';

const storage = getStorage();

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ titre: '', extrait: '', texte: '', image: '', lien: '' });
  const [imageFile, setImageFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadArticles();
  }, []);

  async function loadArticles() {
    setLoading(true);
    try {
      const q = query(collection(db, 'articles'), orderBy('date', 'desc'));
      const snap = await getDocs(q);
      setArticles(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (e) {
      setError('Erreur lors du chargement des articles.');
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleImage(e) {
    setImageFile(e.target.files[0]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    let imageUrl = form.image;
    try {
      if (imageFile) {
        const storageRef = ref(storage, `articles/${Date.now()}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      if (editId) {
        await updateDoc(doc(db, 'articles', editId), {
          ...form,
          image: imageUrl,
          date: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'articles'), {
          ...form,
          image: imageUrl,
          date: serverTimestamp(),
        });
      }
      setForm({ titre: '', extrait: '', texte: '', image: '', lien: '' });
      setImageFile(null);
      setEditId(null);
      setShowForm(false);
      loadArticles();
    } catch (e) {
      setError('Erreur lors de l’enregistrement.');
    }
  }

  function handleEdit(article) {
    setForm({
      titre: article.titre || '',
      extrait: article.extrait || '',
      texte: article.texte || '',
      image: article.image || '',
      lien: article.lien || '',
    });
    setEditId(article.id);
    setShowForm(true);
  }

  async function handleDelete(article) {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await deleteDoc(doc(db, 'articles', article.id));
      if (article.image) {
        const imgRef = ref(storage, article.image);
        try { await deleteObject(imgRef); } catch {}
      }
      loadArticles();
    } catch (e) {
      setError('Erreur lors de la suppression.');
    }
  }

  function handleCancel() {
    setForm({ titre: '', extrait: '', texte: '', image: '', lien: '' });
    setImageFile(null);
    setEditId(null);
    setShowForm(false);
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-white">Gestion des articles</h1>
        <button className="bg-[#FFE400] text-[#001B56] font-bold px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-[#BDFF00] transition" onClick={()=>{setShowForm(!showForm); setEditId(null); setForm({ titre: '', extrait: '', texte: '', image: '', lien: '' });}}>
          <FaPlus /> Nouvel article
        </button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="glass-section rounded-3xl p-6 border border-white/20 shadow-2xl mb-8 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="font-bold text-white">Titre</label>
            <input name="titre" value={form.titre} onChange={handleChange} required className="rounded-xl p-2 bg-white/20 text-white" />
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-bold text-white">Extrait (optionnel)</label>
            <input name="extrait" value={form.extrait} onChange={handleChange} className="rounded-xl p-2 bg-white/20 text-white" />
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-bold text-white">Texte</label>
            <textarea name="texte" value={form.texte} onChange={handleChange} required rows={5} className="rounded-xl p-2 bg-white/20 text-white" />
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-bold text-white">Image</label>
            <input type="file" accept="image/*" onChange={handleImage} className="rounded-xl p-2 bg-white/20 text-white" />
            {form.image && !imageFile && <img src={form.image} alt="aperçu" className="w-32 h-20 object-cover rounded-xl mt-2" />}
            {imageFile && <span className="text-blue-200">{imageFile.name}</span>}
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-bold text-white">Lien (optionnel)</label>
            <input name="lien" value={form.lien} onChange={handleChange} className="rounded-xl p-2 bg-white/20 text-white" />
          </div>
          <div className="flex gap-4 mt-4">
            <button type="submit" className="bg-[#FFE400] text-[#001B56] font-bold px-6 py-2 rounded-xl flex items-center gap-2 hover:bg-[#BDFF00] transition"><FaSave /> Enregistrer</button>
            <button type="button" className="bg-red-500 text-white font-bold px-6 py-2 rounded-xl flex items-center gap-2 hover:bg-red-700 transition" onClick={handleCancel}><FaTimes /> Annuler</button>
          </div>
          {error && <div className="text-red-400 mt-2">{error}</div>}
        </form>
      )}
      {loading ? <div className="text-blue-200">Chargement…</div> : (
        <div className="space-y-6">
          {articles.length === 0 && <div className="text-blue-200">Aucun article pour le moment.</div>}
          {articles.map(article => (
            <div key={article.id} className="glass-section rounded-3xl p-6 border border-white/20 shadow-xl flex flex-col md:flex-row gap-6 items-center">
              {article.image && <img src={article.image} alt={article.titre} className="w-40 h-28 object-cover rounded-xl" />}
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white mb-1">{article.titre}</h2>
                <div className="text-blue-200 text-sm mb-2">{article.date?.toDate ? article.date.toDate().toLocaleString() : ''}</div>
                <div className="text-white mb-2">{article.extrait || article.texte?.slice(0,120)+'…'}</div>
                {article.lien && <a href={article.lien} target="_blank" rel="noopener noreferrer" className="text-[#FFE400] underline font-bold">Lien externe</a>}
              </div>
              <div className="flex flex-col gap-2">
                <button className="bg-blue-500 text-white px-3 py-2 rounded-xl flex items-center gap-2 hover:bg-blue-700 transition" onClick={()=>handleEdit(article)}><FaEdit /> Éditer</button>
                <button className="bg-red-500 text-white px-3 py-2 rounded-xl flex items-center gap-2 hover:bg-red-700 transition" onClick={()=>handleDelete(article)}><FaTrash /> Supprimer</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 